import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { noop } from "ts-essentials";

/** Appends text to the chat's input, leaving whatever is already typed alone. */
type AppendListener = (text: string) => void;

type ChatComposerContextType = {
  /**
   * Whether a chat with a composer is mounted on this surface. Callers hide
   * their "send to the agent" affordance where there is nowhere to send to.
   */
  isComposerAvailable: boolean;
  /**
   * Appends to the chat input rather than replacing it, so a reader can collect
   * several things into one message before sending it.
   */
  appendToComposer: AppendListener;
  /** Called by the chat that owns the composer. Returns the unregister. */
  registerComposer: (listener: AppendListener) => () => void;
};

const ChatComposerContext = createContext<ChatComposerContextType>({
  isComposerAvailable: false,
  appendToComposer: noop,
  registerComposer: () => noop,
});
ChatComposerContext.displayName = "ChatComposerContext";

export const useChatComposerContext = () => useContext(ChatComposerContext);

/**
 * Lets anything on a page reach the page's chat input.
 *
 * Wrap the subtree holding both the chat and whatever wants to write into it —
 * for ads, the editor body, whose comment rail and canvas cards sit beside the
 * chat rather than inside it. Registration is by listener, like
 * `AgentControlProvider`: the chat publishes its composer on mount, and a
 * surface with no chat leaves the default in place, where nothing is available
 * and appending is a no-op.
 */
export const ChatComposerProvider: FC<PropsWithChildren> = ({ children }) => {
  const listeners = useRef(new Set<AppendListener>());
  // Counted in state, not read off the ref: consumers decide whether to render
  // an affordance from this, so a registration has to re-render them.
  const [composerCount, setComposerCount] = useState(0);

  const appendToComposer = useCallback<AppendListener>((text) => {
    for (const listener of listeners.current) {
      listener(text);
    }
  }, []);

  const registerComposer = useCallback((listener: AppendListener) => {
    listeners.current.add(listener);
    setComposerCount((count) => count + 1);

    return () => {
      listeners.current.delete(listener);
      setComposerCount((count) => count - 1);
    };
  }, []);

  const value = useMemo(
    () => ({
      isComposerAvailable: composerCount > 0,
      appendToComposer,
      registerComposer,
    }),
    [composerCount, appendToComposer, registerComposer],
  );

  return (
    <ChatComposerContext.Provider value={value}>
      {children}
    </ChatComposerContext.Provider>
  );
};
